
import React from 'react';
import { MessageSquare } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SidebarSession } from './SidebarSession';
import { SessionType, MessageType } from '@/types/assistant';
import { motion, AnimatePresence } from 'framer-motion';

interface SidebarSessionsListProps {
  filteredSessions: SessionType[];
  currentSession: SessionType | null;
  messages: MessageType[];
  searchQuery: string;
  onSessionClick: (sessionId: string) => void;
  onRenameClick: (e: React.MouseEvent, session: SessionType) => void;
  onDeleteClick: (e: React.MouseEvent, session: SessionType) => void;
}

// Helper function for safe string truncation
const safeTruncateText = (text: string | undefined | null, maxLength: number = 50): string => {
  if (!text || typeof text !== 'string') {
    return 'No content';
  }
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

export const SidebarSessionsList: React.FC<SidebarSessionsListProps> = ({
  filteredSessions,
  currentSession,
  messages,
  searchQuery,
  onSessionClick,
  onRenameClick,
  onDeleteClick
}) => {
  // Get last message from the provided session
  const getLastMessage = (sessionId: string) => {
    if (currentSession?.id === sessionId) {
      // For current session, we have messages loaded
      return messages.length > 0 ? safeTruncateText(messages[messages.length - 1]?.content) : 'No messages yet';
    }
    // For other sessions, we'd need to load messages on demand or provide a better preview system
    return 'Click to view messages';
  };

  if (filteredSessions.length === 0) {
    return (
      <ScrollArea className="flex-1">
        <div className="p-6 flex flex-col items-center justify-center text-center text-muted-foreground">
          <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
          <p className="mb-1">No conversations found</p>
          <p className="text-xs">
            {searchQuery ? "Try a different search term" : "Start a new conversation"}
          </p>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="p-2 space-y-1.5">
        <AnimatePresence>
          {filteredSessions.map((session, index) => {
            const isActive = currentSession?.id === session.id;
            const isNewestSession = index === 0;
            const lastMessageContent = isActive && messages.length > 0 
              ? safeTruncateText(messages[messages.length - 1]?.content)
              : getLastMessage(session.id);
            
            return (
              <SidebarSession
                key={session.id}
                session={session}
                isActive={isActive}
                isNewestSession={isNewestSession}
                lastMessageContent={lastMessageContent}
                onSessionClick={onSessionClick}
                onRenameClick={onRenameClick}
                onDeleteClick={onDeleteClick}
              />
            );
          })}
        </AnimatePresence>
      </div>
    </ScrollArea>
  );
};
